import { useEffect, useRef, useState } from 'react';
import $ from 'jquery';
import uniqueId from 'lodash/uniqueId';

import type RichTextEditor from '../RichTextEditor';
import { richTextEditorManager } from '../RichTextEditorManager';

interface ContentChangeEventDetail {
  content: string;
  layoutHeight: number;
}

interface HeightChangeEventDetail {
  layoutHeight: number;
}

export interface UseRichTextEditorOptions {
  /** The editor's content. Updates are pushed to the editor when this changes. */
  content: string;
  /** Theme to apply. The theme/layout class is only applied when both are present. */
  theme?: string | null;
  layout?: string | null;
  /** Extra classes applied to the editor's content body on creation. */
  extraContentClasses?: string[];
  onContentChange?: (content: string, layoutHeight: number) => void;
  onHeightChange?: (layoutHeight: number) => void;
}

export interface UseRichTextEditorResult {
  /** Attach to the container div the editor should mount into. */
  ref: React.RefObject<HTMLDivElement>;
  /** The underlying editor instance, or null until it has been created. */
  editor: RichTextEditor | null;
}

/**
 * React-friendly wrapper around the jQuery `richTextEditorManager` singleton.
 *
 * Owns the editor lifecycle (create on mount, delete on unmount), bridges the
 * editor's native `rich-text-editor::*` CustomEvents to React callbacks, and keeps
 * the editor's content and theme/layout in sync with the passed options.
 *
 * Because `RichTextEditor` subscribes to `StorytellerReduxStore` directly, this hook
 * must be used within a Redux Provider context.
 */
export function useRichTextEditor({
  content,
  theme,
  layout,
  extraContentClasses,
  onContentChange,
  onHeightChange,
}: UseRichTextEditorOptions): UseRichTextEditorResult {
  const ref = useRef<HTMLDivElement>(null);
  const editorId = useRef(uniqueId()).current;
  const [editor, setEditor] = useState<RichTextEditor | null>(null);

  // Mirror callbacks into refs so the mount-only event listeners always call the
  // latest version without re-subscribing on every render.
  const onContentChangeRef = useRef(onContentChange);
  const onHeightChangeRef = useRef(onHeightChange);
  onContentChangeRef.current = onContentChange;
  onHeightChangeRef.current = onHeightChange;

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    // createEditor requires a jQuery-wrapped element; this is the only jQuery touchpoint
    // and goes away once RichTextEditor itself is converted off jQuery (epic EN-81941).
    const createdEditor = richTextEditorManager.createEditor($(element), editorId, content);

    extraContentClasses?.forEach((extraClass) => createdEditor.addContentClass(extraClass));

    if (theme && layout) {
      createdEditor.applyThemeAndLayoutClass(theme, layout);
    }

    setEditor(createdEditor);

    return () => richTextEditorManager.deleteEditor(editorId);
    // Editor creation/teardown only; content and theme/layout changes are synced by the
    // effects below, so they are intentionally excluded from the dependencies here.
  }, [editorId]);

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    const handleContentChange = (event: Event) => {
      const { content: changedContent, layoutHeight } = (event as CustomEvent<ContentChangeEventDetail>)
        .detail;
      onContentChangeRef.current?.(changedContent, layoutHeight);
    };
    const handleHeightChange = (event: Event) => {
      const { layoutHeight } = (event as CustomEvent<HeightChangeEventDetail>).detail;
      onHeightChangeRef.current?.(layoutHeight);
    };

    element.addEventListener('rich-text-editor::content-change', handleContentChange);
    element.addEventListener('rich-text-editor::height-change', handleHeightChange);

    return () => {
      element.removeEventListener('rich-text-editor::content-change', handleContentChange);
      element.removeEventListener('rich-text-editor::height-change', handleHeightChange);
    };
  }, []);

  useEffect(() => {
    editor?.setContent(content);
  }, [content, editor]);

  useEffect(() => {
    if (editor && theme && layout) {
      editor.applyThemeAndLayoutClass(theme, layout);
    }
  }, [theme, layout, editor]);

  return { ref, editor };
}
